import {useEffect, useRef, useState} from 'react';
import {Card, CardContent, Typography} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import {COLORS} from '../../../../utils/constants';
import EnrolledItem from '../../base/EnrolledItem';

interface IImprovementsChartProps {
    baseline: any[];
    final: any[];
}

const baselineKeys = {
    riskTaking: [
        'entr_orient_risk_t0_entr_orient_a_t0',
        'entr_orient_risk_t0_entr_orient_b_t0',
        'entr_orient_risk_t0_entr_orient_c_t0',
    ],
    innovativeness: [
        'entr_orient_inn_t0_entr_orient_d_t0',
        'entr_orient_inn_t0_entr_orient_e_t0',
        'entr_orient_inn_t0_entr_orient_f_t0',
    ],
    proactiveness: [
        'entr_orient_pro_t0_entr_orient_g_t0',
        'entr_orient_pro_t0_entr_orient_h_t0',
        'entr_orient_pro_t0_entr_orient_i_t0',
    ],
    competitiveAggressiveness: [
        'entr_orient_comp_t0_entr_orient_l_t0',
        'entr_orient_comp_t0_entr_orient_m_t0',
        'entr_orient_comp_t0_entr_orient_n_t0',
    ],
    autonomy: [
        'entr_orient_aut_t0_entr_orient_o_t0',
        'entr_orient_aut_t0_entr_orient_p_t0',
        'entr_orient_aut_t0_entr_orient_q_t0',
        'entr_orient_aut_t0_entr_orient_r_t0',
        'entr_orient_aut_t0_entr_orient_s_t0',
        'entr_orient_aut_t0_entr_orient_t_t0',
    ],
};

const finalKeys = {
    riskTaking: [
        'entr_orient_risk_t1_entr_orient_a_t1',
        'entr_orient_risk_t1_entr_orient_b_t1',
        'entr_orient_risk_t1_entr_orient_c_t1',
    ],
    innovativeness: [
        'entr_orient_inn_t1_entr_orient_d_t1',
        'entr_orient_inn_t1_entr_orient_e_t1',
        'entr_orient_inn_t1_entr_orient_f_t1',
    ],
    proactiveness: [
        'entr_orient_proact_t1_entr_orient_g_t1',
        'entr_orient_proact_t1_entr_orient_h_t1',
        'entr_orient_proact_t1_entr_orient_i_t1',
    ],
    competitiveAggressiveness: [
        'entr_orient_compet_t1_entr_orient_l_t1',
        'entr_orient_compet_t1_entr_orient_m_t1',
        'entr_orient_compet_t1_entr_orient_n_t1',
    ],
    autonomy: [
        'entr_orient_aut_t1_entr_orient_o_t1',
        'entr_orient_aut_t1_entr_orient_p_t1',
        'entr_orient_aut_t1_entr_orient_q_t1',
        'entr_orient_aut_t1_entr_orient_r_t1',
        'entr_orient_aut_t1_entr_orient_s_t1',
        'entr_orient_aut_t1_entr_orient_t_t1',
    ],
};

const keys: ('riskTaking' | 'innovativeness' | 'proactiveness' | 'competitiveAggressiveness' | 'autonomy')[] = [
    'riskTaking',
    'innovativeness',
    'proactiveness',
    'competitiveAggressiveness',
    'autonomy',
];

const translations = {
    riskTaking: 'RISK-TAKING',
    innovativeness: 'INNOVATIVENESS',
    proactiveness: 'PROACTIVENESS',
    competitiveAggressiveness: 'COMPETITIVE AGGRESSIVENESS',
    autonomy: 'AUTONOMY',
};

const ImprovementsChart = ({baseline, final}: IImprovementsChartProps) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [improvedPercentage, setImprovedPercentage] = useState<number>(0);

    const calcSum = (keys: string[], submission: any) => {
        const tempKeys = keys.map((key) => Number(submission[key]));

        return (tempKeys.reduce((partialSum, a) => partialSum + a, 0) * 1.0) / tempKeys.length;
    };

    function calculateImprovements(baselineSubmissions: any[], finalSubmissions: any[]) {
        const initObject = {
            riskTaking: 0,
            innovativeness: 0,
            proactiveness: 0,
            competitiveAggressiveness: 0,
            autonomy: 0,
        };

        const finalDataImprovements = {...initObject};
        const baselineData = {...initObject};
        const finalData = {...initObject};

        let improvedFinalCount = 0;

        finalSubmissions.forEach((submission: any) => {
            const baselineSubmission = baselineSubmissions.find((sub: any) => sub.uoa_id === submission.uoa_id);

            let currentValue = {...initObject};
            let baselineValue = {...initObject};

            keys.forEach((element) => {
                currentValue[element] = calcSum(finalKeys[element], submission);
                baselineValue[element] = calcSum(baselineKeys[element], baselineSubmission);
            });

            const currentMean = Object.values(currentValue).reduce((partialSum, a) => partialSum + a, 0) / keys.length;
            const baselineMean =
                Object.values(baselineValue).reduce((partialSum, a) => partialSum + a, 0) / keys.length;

            if (currentMean > baselineMean) {
                improvedFinalCount++;

                keys.forEach((element) => {
                    finalData[element] += currentValue[element];
                    baselineData[element] += baselineValue[element];

                    if (currentValue[element] > baselineValue[element]) {
                        finalDataImprovements[element]++;
                    }
                });
            }
        });

        keys.forEach((element) => {
            finalData[element] /= improvedFinalCount;
            baselineData[element] /= improvedFinalCount;
            finalDataImprovements[element] /= improvedFinalCount;
        });

        const finalDataImprovedPercentage = (improvedFinalCount / finalSubmissions.length) * 100;

        return {baselineData, finalData, finalDataImprovements, finalDataImprovedPercentage};
    }

    const calculateSubmissions = (baselineSubmissions: any[], finalSubmissions: any[]) => {
        const {baselineData, finalData, finalDataImprovements, finalDataImprovedPercentage} = calculateImprovements(
            baselineSubmissions,
            finalSubmissions
        );

        const dashboardOptions: Highcharts.Options = {
            chart: {
                polar: true,
                style: {
                    fontFamily: 'Rubik, sans-serif',
                },
                backgroundColor: '#fafafa',
            },
            title: {
                text: '',
                align: 'left',
            },
            xAxis: {
                categories: Object.values(translations),
            },
            yAxis: {
                min: 0,
            },
            legend: {
                verticalAlign: 'top',
            },
            series: [
                {
                    type: 'line',
                    name: 'BASELINE',
                    data: Object.values(baselineData).map((data) => Number(data.toFixed(1))),
                    color: COLORS.blue,
                },
                {
                    type: 'line',
                    name: 'FINAL',
                    data: Object.values(finalData).map((data) => Number(data.toFixed(1))),
                    color: COLORS.yellow,
                },
            ],
        };

        const improvementRatio: any = {};

        keys.forEach((element) => {
            improvementRatio[element] = finalData[element] / baselineData[element] - 1;
        });

        return {dashboardOptions, improvementRatio, finalDataImprovements, finalDataImprovedPercentage};
    };

    useEffect(() => {
        const {dashboardOptions, improvementRatio, finalDataImprovements, finalDataImprovedPercentage} =
            calculateSubmissions(baseline, final);

        setColumns([
            {field: 'name', headerName: 'Skill', flex: 2},
            {field: 'improved', headerName: '% Improved', flex: 1},
            {field: 'improvement', headerName: '% Improvement', flex: 1},
        ]);

        setRows(
            keys.map((element) => ({
                id: keys.indexOf(element) + 1,
                name: translations[element],
                improvement: (improvementRatio[element] * 100).toFixed(1) + ' %',
                improved: (finalDataImprovements[element] * 100).toFixed(1) + ' %',
            }))
        );

        setImprovedPercentage(finalDataImprovedPercentage);

        setOptions(dashboardOptions);
    }, []);

    return (
        <>
            <Card className='chart-container'>
                <CardContent>
                    <div className='enrolled-item space-x-4'>
                        <EnrolledItem
                            text='% of participants who increased their entrepreneurial mindset'
                            value={improvedPercentage.toFixed(1)}
                        />
                    </div>
                </CardContent>
            </Card>
            <br />

            <Card>
                <CardContent>
                    <Typography variant='h6'>Level of increase in entrepreneurial mindset</Typography>
                    <div className='flex justify-between flex-wrap'>
                        <Card
                            elevation={0}
                            className='basis-full lg:basis-1/2 pa-4'
                            sx={{border: '1px solid #E0E0E0', borderRadius: '4px'}}
                        >
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={options}
                                ref={chartComponentRef}
                            />
                        </Card>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            hideFooterPagination={true}
                            hideFooter={true}
                            className='basis-full lg:basis-1/2'
                        />
                    </div>
                </CardContent>
            </Card>
        </>
    );
};

export default ImprovementsChart;
